/**
 * SED D3.4 — explicit off-territory coverage classification.
 *
 * The data-provider fleet is US-federal-centric: ~12 of the catalogued
 * providers are US-only (`US_ONLY_PROVIDER_IDS`). The dispatcher already
 * skips those for non-US sites and stamps `coverageMode: 'global-degraded'`,
 * but the classification is BINARY — a crude lat/lng bounding box (CONUS +
 * AK + HI) that mis-classifies US territories (Puerto Rico, Guam, USVI,
 * American Samoa, Northern Mariana Islands) and offers no signal for
 * tribal-trust land where federal-provider coverage is partial.
 *
 * This module replaces the binary box with a granular classifier so the
 * degradation can be surfaced EXPLICITLY (as a `DataGap` + a richer
 * `coverageMode`) instead of silently. It is PURE and side-effect free —
 * no network, no imports beyond the type it returns — fully unit-testable.
 *
 * IMPORTANT (constitutional, see spec risks): the granular classes do NOT
 * change which providers are skipped. The dispatcher's US-only skip stays
 * keyed off the proven US-box (`isUSCoordinate`) so we never NEWLY skip a
 * provider that currently runs. `'tribal-trust-uncertain'` is a SOFT
 * advisory class (a gap note, not a hard provider-skip) because tribal-trust
 * land has no clean lat/lng boundary — over-claiming it would be a worse
 * honesty failure than the current binary.
 */

/**
 * Fine-grained territory classification of a coordinate.
 *  - `'conus'`                 — contiguous lower-48 (+ DC). Full coverage.
 *  - `'ak-hi'`                 — Alaska / Hawaii. Full coverage (the US-box
 *                                already covers these).
 *  - `'us-territory'`          — PR / Guam / USVI / American Samoa / N.
 *                                Mariana Islands. In the US, but the federal
 *                                provider fleet has partial coverage.
 *  - `'tribal-trust-uncertain'`— soft advisory: a coordinate that MAY sit on
 *                                tribal-trust land (no clean lat/lng test;
 *                                reserved for a future BIA-registry hook).
 *                                Never emitted by the lat/lng classifier
 *                                today — declared so callers can be widened
 *                                without a second schema change.
 *  - `'non-us'`                — outside US coverage entirely.
 */
export type TerritoryClass =
  | 'conus'
  | 'ak-hi'
  | 'us-territory'
  | 'tribal-trust-uncertain'
  | 'non-us';

/** A bounding box `[latMin, latMax, lngMin, lngMax]`. */
type Box = readonly [number, number, number, number];

const CONUS: Box = [24, 50, -125, -66];
const ALASKA: Box = [51, 72, -180, -129];
const HAWAII: Box = [18, 23, -161, -154];

/**
 * US-territory boxes. Coarse by design — the goal is to classify the site
 * as "US territory (partial federal coverage)" rather than mis-label it
 * `'non-us'`. False positives here are far less harmful than the status quo
 * (a PR site reading as fully non-US).
 */
const TERRITORY_BOXES: ReadonlyArray<{ box: Box; name: string }> = [
  { box: [17.5, 18.6, -67.5, -65.2], name: 'Puerto Rico' },
  { box: [17.6, 18.5, -65.1, -64.5], name: 'US Virgin Islands' },
  { box: [13.2, 13.7, 144.6, 145.0], name: 'Guam' },
  { box: [14.0, 20.6, 144.8, 146.1], name: 'Northern Mariana Islands' },
  { box: [-14.4, -14.1, -170.9, -169.4], name: 'American Samoa' },
];

function inBox(lat: number, lng: number, [latMin, latMax, lngMin, lngMax]: Box): boolean {
  return lat >= latMin && lat <= latMax && lng >= lngMin && lng <= lngMax;
}

/**
 * Classify a coordinate into a `TerritoryClass`. Pure + deterministic.
 *
 * Non-finite inputs default to `'non-us'` (fail-safe: we never claim US
 * coverage for a coordinate we can't place).
 */
export function classifyTerritory(lat: number, lng: number): TerritoryClass {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return 'non-us';
  if (inBox(lat, lng, CONUS)) return 'conus';
  if (inBox(lat, lng, ALASKA) || inBox(lat, lng, HAWAII)) return 'ak-hi';
  for (const { box } of TERRITORY_BOXES) {
    if (inBox(lat, lng, box)) return 'us-territory';
  }
  return 'non-us';
}

/**
 * True when the territory class has FULL US-federal provider coverage
 * (CONUS / AK / HI). Mirrors the proven `isUSCoordinate` box so the
 * provider-skip decision is unchanged.
 */
export function territoryHasFullUsCoverage(t: TerritoryClass): boolean {
  return t === 'conus' || t === 'ak-hi';
}

/**
 * The `coverageMode` a territory class maps to. CONUS/AK/HI → `'US'`;
 * US territory → `'us-territory-degraded'`; everything else →
 * `'global-degraded'`. (`'tribal-degraded'` is reserved for the future
 * BIA-registry hook; the lat/lng classifier never returns it.)
 */
export function coverageModeForTerritory(
  t: TerritoryClass,
): 'US' | 'global-degraded' | 'us-territory-degraded' | 'tribal-degraded' {
  switch (t) {
    case 'conus':
    case 'ak-hi':
      return 'US';
    case 'us-territory':
      return 'us-territory-degraded';
    case 'tribal-trust-uncertain':
      return 'tribal-degraded';
    case 'non-us':
      return 'global-degraded';
  }
}

/** A coverage-degradation rollup, mirroring the SED field shape. */
export interface CoverageDegradation {
  mode: 'global-degraded' | 'us-territory-degraded' | 'tribal-degraded';
  territory?: TerritoryClass;
  usOnlyProvidersSkipped: string[];
  affectedSections: string[];
  note: string;
}

/**
 * Map a skipped US-only provider id to the SED section(s) it feeds, so the
 * degradation rollup can name which sections went dark. Conservative —
 * unknown providers contribute no section.
 */
const PROVIDER_TO_SECTIONS: Record<string, string[]> = {
  'hifld-grid': ['grid'],
  'fema-flood': ['flood'],
  'usfws-env': ['environmental'],
  'nrel-solar': ['solar'],
  'nrel-wind': ['wind'],
  'eia-electricity': ['electricity'],
  'eia-natural-gas': ['fuelSupply'],
  'airnow-aqi': ['airQuality'],
  'nrc-reactors': ['nuclearInfra'],
  'nrc-adams': ['adamsDocuments'],
  'regulatory-db': ['regulatory'],
  'epa-attainment': ['environmental'],
};

/**
 * Build the `CoverageDegradation` rollup for a degraded site.
 *
 * Returns `null` for a fully-covered (`'conus'` / `'ak-hi'`) territory —
 * there's nothing to degrade, so the SED carries no `coverageDegradation`
 * (additive: US sites are byte-identical to today).
 *
 * `skippedProviderIds` is the exact list of US-only providers the
 * dispatcher skipped for this site (so the rollup reflects reality rather
 * than the full catalogue). Pure + deterministic.
 */
export function buildCoverageDegradation(
  territory: TerritoryClass,
  skippedProviderIds: ReadonlyArray<string>,
): CoverageDegradation | null {
  if (territoryHasFullUsCoverage(territory)) return null;
  const mode = coverageModeForTerritory(territory);
  if (mode === 'US') return null;

  const skipped = Array.from(new Set(skippedProviderIds)).sort();
  const affected = new Set<string>();
  for (const id of skipped) {
    for (const section of PROVIDER_TO_SECTIONS[id] ?? []) affected.add(section);
  }
  const affectedSections = Array.from(affected).sort();

  const territoryLabel =
    territory === 'us-territory'
      ? 'a US territory'
      : territory === 'tribal-trust-uncertain'
        ? 'possible tribal-trust land'
        : 'outside US coverage';
  const note =
    skipped.length > 0
      ? `Site is ${territoryLabel}; ${skipped.length} US-federal provider(s) skipped (${skipped.join(', ')}), affecting ${affectedSections.length} section(s). Evidence for those sections is explicitly degraded, not silently absent.`
      : `Site is ${territoryLabel}; US-federal provider coverage is degraded for this location.`;

  return { mode, territory, usOnlyProvidersSkipped: skipped, affectedSections, note };
}
